import React, { useState, useEffect, useContext } from "react";
import "../../LoadProfile/LoadProfile.css";
import { useSearchParams } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { Box, CircularProgress, Typography, Divider } from "@mui/material";
import PrimaryTextField from "../../Inputs/PrimaryTextField/PrimaryTextField";
import PrimarySelect from "../../Inputs/PrimarySelect/PrimarySelect";
import PrimaryButton from "../../Buttons/PrimaryButton/PrimaryButton";
import SecondaryButton from "../../Buttons/SecondaryButton/SecondaryButton";
import StandAloneRadio from "../../Buttons/StandaloneRadio/StandAloneRadio";
import isEmailValidator from "validator/lib/isEmail";
import { parsePhoneNumber, formatPhoneNumber } from "../../../utils/formatHelpers";
import { sortArrayOfStringObjectsAsc } from "../../../utils/sortHelpers";
import { handleAltEnter } from "../../../utils/keyboardShortcutHelpers";
import { CarrierService } from "../../../services/CarrierService";
import { MovementService } from "../../../services/MovementService";
import { LoadService } from "../../../services/LoadService";
import { SecurityService } from "../../../services/SecurityService";
import { AlertContext } from "../../Dashboard/Dashboard";

export default function LoadContactsModal() {
  const { instance } = useMsal();
  const { warning } = useContext(AlertContext);
  let [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [carrierId, setCarrierId] = useState("");
  const [movement, setMovement] = useState({});
  const [phone, setPhone] = useState("");
  const [phoneExt, setPhoneExt] = useState("");
  const [selectedPhoneType, setSelectedPhoneType] = useState("P");
  const [phoneValid, setPhoneValid] = useState(true);
  const [primaryPhone, setPrimaryPhone] = useState(false);
  const [tractor, setTractor] = useState("");
  const [trailer, setTrailer] = useState("");
  const [confirmationNumber, setConfirmationNumber] = useState("");
  const [email, setEmail] = useState("");
  const [emailValid, setEmailValid] = useState(true);
  const [driverName, setDriverName] = useState("");
  const [driverPhone, setDriverPhone] = useState("");
  const [contacts, setContacts] = useState([]);
  const [selectedContact, setSelectedContact] = useState("");
  const [contactName, setContactName] = useState("");
  const [dispatchers, setDispatchers] = useState([]);
  const [selectedDispatcher, setSelectedDispatcher] = useState("");
  const [submitDisabled, setSubmitDisabled] = useState(true);

  const phoneTypes = [
    {
      value: "P",
      text: "Phone"
    },
    {
      value: "M",
      text: "Mobile"
    }
  ];

  const concatPhone = (phone, ext) => {
    if (ext?.length) {
      return `${phone}x${ext}`;
    } else {
      return phone;
    }
  };

  const validatePhone = (number) => {
    if (number) {
      return parseInt(number.length) >= 12;
    } else {
      return null;
    }
  };

  const handleClose = () => {
    window.close();
  };

  const handleCancel = () => {
    handleClose();
  };

  const handleSave = () => {
    let updatedMovement = movement;
    updatedMovement.carrierPhone = concatPhone(phone, phoneExt);
    updatedMovement.carrierTractor = tractor;
    updatedMovement.carrierTrailer = trailer;
    updatedMovement.confirmationNumber = confirmationNumber;
    updatedMovement.carrierEmail = email;
    updatedMovement.driverName = driverName;
    updatedMovement.driverCell = driverPhone;
    updatedMovement.carrierContact = contactName;
    updatedMovement.dispatcherId = selectedDispatcher;

    setSubmitDisabled(true);
    setIsLoading(true);
    MovementService.UpdateMovement(instance, updatedMovement).then(res => {
      setMovement(res);
      setIsLoading(false);
      setSubmitDisabled(false);
      handleClose();
    }).catch(error => {
      console.error(error);
      setIsLoading(false);
      setSubmitDisabled(false);
      handleClose();
      warning(error);
    });
  };

  const handleContactInfo = (contactId) => {
    const contact = contacts.find(({ id }) => id == contactId);

    setContactName(contact.name);
    setEmail(contact.email ?? "");
    setPrimaryPhone(selectedPhoneType === contact.primaryPhone);
    if (selectedPhoneType === "P" && contact.phone) {
      setPhone(formatPhoneNumber(parsePhoneNumber(contact.phone, "phone")));
      setPhoneExt(parsePhoneNumber(contact.phone, "ext"));
    } else if (selectedPhoneType === "M" && contact.mobile) {
      setPhone(formatPhoneNumber(parsePhoneNumber(contact.mobile, "phone")));
      setPhoneExt(parsePhoneNumber(contact.mobile, "ext"));
    } else {
      setPhone("");
      setPhoneExt("");
    }
  };

  const handlePhoneTypeChange = value => {
    setSelectedPhoneType(value);
    handleContactInfo(selectedContact);
  };

  const handleSelectedContactChange = value => {
    setSelectedContact(value);
    handleContactInfo(value);
  };

  useEffect(() => {
    setSubmitDisabled(
      !phone ||
      !validatePhone(phone) ||
      !email ||
      !isEmailValidator(email) ||
      !selectedContact ||
      !selectedDispatcher);

    setEmailValid(isEmailValidator(email));
    setPhoneValid(validatePhone(phone));
  }, [
    phone,
    email,
    selectedContact,
    selectedDispatcher
  ]);

  useEffect(() => {
    if (carrierId && movement) {
      CarrierService.GetCarrierContacts(instance, carrierId).then(res => {
        setContacts(res);
        if (res.length && res.some(({ name }) => name == movement.carrierContact)) {
          const contact = res.find(({ name }) => name == movement.carrierContact);
          setSelectedContact(contact.id);
          setContactName(contact.name);

          const phoneType = contact.mobile === movement.carrierPhone ? "M" : "P";
          setSelectedPhoneType(phoneType);
          setPrimaryPhone(phoneType === contact.primaryPhone);
        }
      }).catch(error => {
        console.error(error);
        setIsLoading(false);
        warning(error);
      });

      if (dispatchers.length > 0 && dispatchers.some(({ userIdMcld }) => userIdMcld == movement.dispatcherId)) setSelectedDispatcher(movement.dispatcherId);

      setPhone(formatPhoneNumber(parsePhoneNumber(movement.carrierPhone, "phone")));
      setPhoneExt(parsePhoneNumber(movement.carrierPhone, "ext"));
      setTractor(movement.carrierTractor);
      setTrailer(movement.carrierTrailer);
      setConfirmationNumber(movement.confirmationNumber);
      if (movement.carrierEmail) setEmail(movement.carrierEmail);
      setDriverName(movement.driverName);
      setDriverPhone(formatPhoneNumber(parsePhoneNumber(movement.driverCell, "phone")));
    }
  }, [carrierId, movement, dispatchers]);

  useEffect(() => {
    const loadId = searchParams.get("loadId");
    if (loadId) {
      setIsLoading(true);
      LoadService.GetLoad(instance, loadId).then(res => {
        setMovement(res.movements ? res.movements[0] : {});
        setIsLoading(false);
      }).catch(error => {
        console.error(error);
        setIsLoading(false);
        warning(error);
      });
    }

    setCarrierId(searchParams.get("carrierId"));

    SecurityService.GetUsers(instance).then(res => {
      let sortedUsers = sortArrayOfStringObjectsAsc(res, "userIdMcld");
      setDispatchers(sortedUsers);
    }).catch(error => {
      console.error(error);
      warning(error);
    });
  }, []);

  return (
    <Box onKeyDown={(e) => handleAltEnter(e, submitDisabled, handleSave)} sx={{ margin: "10px" }}>
      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", marginTop: "20px" }}>
          <CircularProgress sx={{ color: "active.main" }} size={40} />
        </Box>
      ) : (
        <Box>
          <Box sx={{ marginBottom: "15px" }}>
            <Box sx={{ marginBottom: "5px" }}>
              <Typography variant="h2" color="black_opacity.100">
                Edit Load Contacts
              </Typography>
            </Box>
            <Divider />
          </Box>
          <Box>
            <Box className="Load-modal-input-row">
              <PrimaryTextField
                label="Contact Phone *"
                value={phone}
                onChange={setPhone}
                placeholder="123-456-7890"
                error={!phone || !phoneValid}
                isPhone
                width="130px"
              />
              <Box sx={{ marginLeft: "16px" }}>
                <PrimaryTextField
                  label="Ext."
                  width="50px"
                  value={phoneExt}
                  onChange={setPhoneExt}
                  isNumber
                  fullWidth
                />
              </Box>
              <Box sx={{ marginLeft: "16px" }}>
                <PrimarySelect
                  options={phoneTypes}
                  onChange={handlePhoneTypeChange}
                  value={selectedPhoneType}
                  nameKey="text"
                  valueKey="value"
                  width="90px"
                  disabled={isLoading}
                />
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", marginLeft: "5px" }}>
                <StandAloneRadio
                  checked={primaryPhone}
                  value=""
                  onChange={() => { }}
                  disabled
                />
                <Typography variant="body2" color="black_solid.60">Primary<br />Phone</Typography>
              </Box>
            </Box>
          </Box>
          <Box className="Load-modal-input-row">
            <PrimaryTextField
              label="Tractor #"
              value={tractor}
              onChange={setTractor}
              width="190px"
            />
            <Box sx={{ marginLeft: "16px" }}>
              <PrimaryTextField
                label="Trailer #"
                value={trailer}
                onChange={setTrailer}
                width="190px"
              />
            </Box>
          </Box>
          <Box className="Load-modal-input-row">
            <PrimaryTextField
              label="Pro / Order Confirmation Number"
              value={confirmationNumber}
              onChange={setConfirmationNumber}
              width="400px"
            />
          </Box>
          <Box className="Load-modal-input-row">
            <PrimaryTextField
              label="Carrier Email*"
              value={email}
              onChange={setEmail}
              error={!email || !emailValid}
              width="400px"
            />
          </Box>
          <Box className="Load-modal-input-row">
            <PrimaryTextField
              label="Driver Name"
              value={driverName}
              onChange={setDriverName}
              width="190px"
            />
            <Box sx={{ marginLeft: "16px" }}>
              <PrimaryTextField
                label="Driver Phone"
                value={driverPhone}
                onChange={setDriverPhone}
                placeholder="123-456-7890"
                isPhone
                width="190px"
              />
            </Box>
          </Box>
          <Box className="Load-modal-input-row">
            <PrimarySelect
              label="Primary Carrier Contact *"
              options={contacts}
              onChange={handleSelectedContactChange}
              value={selectedContact}
              nameKey="name"
              valueKey="id"
              width="400px"
              disabled={isLoading}
              error={!selectedContact}
            />
          </Box>
          <Box className="Load-modal-input-row">
            <PrimarySelect
              label="Capstone Carrier Rep *"
              options={dispatchers}
              onChange={setSelectedDispatcher}
              value={selectedDispatcher}
              nameKey="userName"
              valueKey="userIdMcld"
              width="400px"
              disabled={isLoading}
              error={!selectedDispatcher}
            />
          </Box>
          <Box sx={{ display: "flex", width: "400px" }}>
            <Box sx={{ marginLeft: "auto" }}>
              <SecondaryButton
                label="Cancel"
                onClick={handleCancel}
                disabled={isLoading}
              />
            </Box>
            <Box sx={{ marginLeft: "20px" }}>
              <PrimaryButton
                label="Submit"
                width="64px"
                onClick={handleSave}
                isLoading={isLoading}
                disabled={submitDisabled}
              />
            </Box>
          </Box>
        </Box>
      )
      }
    </Box>
  );
}

LoadContactsModal.propTypes = {};